/**
 * OrdersPage (Мои заказы) — the user's order history (ТЗ §23).
 *
 * Lists orders (server object-scoped) with status, total and date. Each row offers
 * **Открыть** (the order screen + chat); completed-and-not-yet-reviewed orders also offer
 * **Оставить отзыв**, opening the review sheet inline (``can_review`` is server-computed).
 * After a review is posted the list refetches so the button disappears.
 */
import { Button } from "@melusine/shared-ui";
import { useBackButton } from "@melusine/shared-telegram";
import {
  ORDER_STATUS_PRESENTATION,
  OrderStatusBadge,
  orderThumbnail,
  useMyOrders,
  type OrderListItem,
  type OrderStatus,
} from "@entities/order";
import { OrderCancelControl, useSelfCancel } from "@features/order-cancel";
import { ReceiptControl } from "@features/order-receipt";
import { ReviewSheet } from "@features/review";
import { StatePanel } from "@widgets/catalog-browser";
import { routes, useNavigate } from "@shared/router";
import { useMemo, useState } from "react";

import styles from "./OrdersPage.module.css";

/** A status filter: ``null`` means "all". */
type StatusFilter = OrderStatus | null;

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function OrdersPage(): JSX.Element {
  const navigate = useNavigate();
  const { data: orders, isLoading, error, refetch } = useMyOrders();
  const [filter, setFilter] = useState<StatusFilter>(null);
  const [reviewOrder, setReviewOrder] = useState<OrderListItem | null>(null);

  useBackButton(() => {
    navigate(routes.home());
  });

  // The distinct statuses actually present in the user's orders, in canonical order.
  const availableStatuses = useMemo<readonly OrderStatus[]>(() => {
    const present = new Set((orders ?? []).map((o) => o.status));
    return (Object.keys(ORDER_STATUS_PRESENTATION) as OrderStatus[]).filter((s) =>
      present.has(s),
    );
  }, [orders]);

  // Cycle: Все → first present status → … → back to Все.
  const cycleFilter = (): void => {
    setFilter((current) => {
      if (availableStatuses.length === 0) {
        return null;
      }
      if (current === null) {
        return availableStatuses[0] ?? null;
      }
      const idx = availableStatuses.indexOf(current);
      return idx === -1 || idx + 1 >= availableStatuses.length
        ? null
        : availableStatuses[idx + 1] ?? null;
    });
  };

  const filterLabel =
    filter === null ? "Все" : ORDER_STATUS_PRESENTATION[filter].label;

  const visibleOrders = useMemo(
    () => (orders ?? []).filter((o) => filter === null || o.status === filter),
    [orders, filter],
  );

  if (isLoading) {
    return <StatePanel kind="loading" />;
  }
  if (error) {
    return (
      <StatePanel kind="error" message="Не удалось загрузить заказы." onRetry={refetch} />
    );
  }
  if (!orders || orders.length === 0) {
    return (
      <div className={styles.page}>
        <Header label={filterLabel} onToggle={cycleFilter} canFilter={false} />
        <StatePanel kind="empty" message="У вас пока нет заказов" />
        <Button variant="primary" size="lg" fullWidth onClick={() => navigate(routes.shop())}>
          В магазин
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <Header
        label={filterLabel}
        onToggle={cycleFilter}
        canFilter={availableStatuses.length > 1}
      />
      {visibleOrders.length === 0 ? (
        <StatePanel
          kind="empty"
          message={`Нет заказов со статусом «${filterLabel}»`}
        />
      ) : null}
      <div className={styles.list}>
        {visibleOrders.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            onOpen={() => navigate(routes.order(order.id))}
            onReview={() => setReviewOrder(order)}
            onRefetch={refetch}
            onOpenSupport={() => navigate(routes.support())}
          />
        ))}
      </div>

      {reviewOrder !== null ? (
        <ReviewSheet
          open
          orderId={reviewOrder.id}
          orderNumber={reviewOrder.number}
          createdAt={reviewOrder.created_at}
          onClose={() => setReviewOrder(null)}
          onSubmitted={() => {
            setReviewOrder(null);
            refetch();
          }}
        />
      ) : null}
    </div>
  );
}

interface OrderCardProps {
  readonly order: OrderListItem;
  readonly onOpen: () => void;
  readonly onReview: () => void;
  readonly onRefetch: () => void;
  readonly onOpenSupport: () => void;
}

function OrderCard({
  order,
  onOpen,
  onReview,
  onRefetch,
  onOpenSupport,
}: OrderCardProps): JSX.Element {
  // Self-cancel button + live countdown, server-authoritative (ТЗ §11).
  const selfCancel = useSelfCancel({
    orderId: order.id,
    status: order.status,
    canSelfCancel: order.can_self_cancel,
    secondsLeft: order.self_cancel_seconds_left,
    onCancelled: onRefetch,
  });

  // Thumbnail of the headline (priciest) line + «+N» badge for multi-item orders (ТЗ §7).
  const thumb = orderThumbnail(order.items);

  return (
    <article className={styles.card}>
      <div className={styles.head}>
        <div className={styles.headMain}>
          <span className={styles.number}>Заказ {order.number}</span>
          <span className={styles.date}>{formatDate(order.created_at)}</span>
        </div>
        <OrderStatusBadge status={order.status} />
      </div>

      <div className={styles.product}>
        <span className={styles.thumb}>
          {thumb.imageUrl ? (
            <img
              src={thumb.imageUrl}
              alt=""
              className={styles.thumbImg}
              loading="lazy"
            />
          ) : (
            <span className={styles.thumbFallback} aria-hidden="true">
              ✦
            </span>
          )}
          {thumb.extraCount > 0 ? (
            <span className={styles.thumbBadge}>+{thumb.extraCount}</span>
          ) : null}
        </span>
        <span className={styles.productLabel}>
          {thumb.title || `Заказ ${order.number}`}
        </span>
      </div>

      <div className={styles.footer}>
        <span className={styles.total}>
          {Number(order.total_rub).toLocaleString("ru-RU")} ₽
        </span>
        <div className={styles.actions}>
          {order.can_review ? (
            <button type="button" className={styles.review} onClick={onReview}>
              Оставить отзыв
            </button>
          ) : null}
          <button type="button" className={styles.open} onClick={onOpen}>
            Открыть
          </button>
        </div>
      </div>

      <OrderCancelControl state={selfCancel} variant="compact" />

      <ReceiptControl
        orderId={order.id}
        status={order.status}
        canRequest={order.can_request_receipt}
        state={order.receipt_request_state}
        onRequested={onRefetch}
        onOpenSupport={onOpenSupport}
        variant="compact"
      />
    </article>
  );
}

interface HeaderProps {
  readonly label: string;
  readonly onToggle: () => void;
  /** Disable the control when there's nothing meaningful to filter. */
  readonly canFilter: boolean;
}

function Header({ label, onToggle, canFilter }: HeaderProps): JSX.Element {
  const active = label !== "Все";
  return (
    <div className={styles.topbar}>
      <h1 className={styles.title}>Мои заказы</h1>
      <button
        type="button"
        className={styles.filter}
        data-active={active}
        disabled={!canFilter}
        aria-label={`Фильтр по статусу: ${label}`}
        onClick={onToggle}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M3 5h18" />
          <path d="M6 12h12" />
          <path d="M10 19h4" />
        </svg>
        <span>{label}</span>
      </button>
    </div>
  );
}
